import { TourContext, TourGateState, TourRegistration } from '../types';
import { isEligibleOnRoute } from './route';
import { isRunning } from './status';

/**
 * Is this tour currently competing for the screen — running AND still passing its own gate?
 *
 * Exported because more than one caller needs the question and the two must not drift. The
 * habitat onboardings gate themselves on the guided journey not competing, and asking only
 * `deriveStatus(...) === 'active'` there would be a bug: a user whose `guidedExperience`
 * feature lapses mid-journey keeps a stepHistory with no completedAt/skippedAt, so the journey
 * reads `active` forever and would block habitat onboarding permanently. Its `isEligible` is
 * what actually retires it, so the check has to include that.
 *
 * `pending` counts as not competing: it is "cannot decide yet", never "yes".
 */
export const isCompeting = (
  reg: TourRegistration,
  gate: TourGateState,
  ctx: TourContext | undefined,
  pathname: string,
): boolean => {
  if (!isRunning(gate.tours[reg.id])) return false;
  if (!reg.isEligible) return true;
  return reg.isEligible(gate, ctx, pathname) === true;
};

/**
 * Pure arbitration: pick which registered tour surfaces on `pathname`.
 * candidates = active (not cleared) AND route-eligible AND passing any extra gate.
 * An exclusive candidate suppresses the rest of the screen; otherwise top priority wins.
 */
const arbitrate = (
  registrations: TourRegistration[],
  gate: TourGateState,
  pathname: string,
  ctx?: TourContext,
): TourRegistration | undefined => {
  const candidates = registrations.filter(
    (reg) => isEligibleOnRoute(reg.scope, pathname) && isCompeting(reg, gate, ctx, pathname),
  );
  if (candidates.length === 0) return undefined;

  const exclusive = candidates.filter((c) => c.exclusive);
  const pool = exclusive.length > 0 ? exclusive : candidates;

  return pool.reduce((best, c) => (c.priority > best.priority ? c : best));
};

export default arbitrate;
